import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import ConveniosService from "../../services/ConveniosService";
import EstudiosService from "../../services/EstudiosService";
import SolicitudesEstudiosService from "../../services/SolicitudesEstudiosService";
import CajaService from "../../services/CajaService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileExcel } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const ConveniosSolicitudes = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = usuarioString ? JSON.parse(usuarioString) : {};

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [catalogEstudiosMap, setCatalogEstudiosMap] = useState({});
  const [cajaMap, setCajaMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros principales
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState("todas");
  const [estadoPagoSeleccionado, setEstadoPagoSeleccionado] = useState("Todos");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Checkboxes Adicionales
  const [chkMostrarDetalleSolicitud, setChkMostrarDetalleSolicitud] = useState(true); // Seleccionado por defecto
  const [chkOcultarEstadoPago, setChkOcultarEstadoPago] = useState(false);

  useEffect(() => {
    document.title = "Convenios en Solicitudes";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resConv, resCatEst, resCaja] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        ConveniosService.getConvenios().catch(() => ({ data: [] })),
        EstudiosService.getEstudios().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaConv = Array.isArray(resConv?.data?.convenios)
        ? resConv.data.convenios
        : Array.isArray(resConv?.data)
        ? resConv.data
        : [];

      const listaCatEst = Array.isArray(resCatEst?.data?.estudios)
        ? resCatEst.data.estudios
        : Array.isArray(resCatEst?.data)
        ? resCatEst.data
        : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.caja)
        ? resCaja.caja
        : Array.isArray(resCaja?.data)
        ? resCaja.data
        : [];

      const catMap = {};
      listaCatEst.forEach((est) => {
        if (est && (est.idEstudio || est.id)) {
          catMap[est.idEstudio || est.id] = est;
        }
      });
      setCatalogEstudiosMap(catMap);

      const cMap = {};
      listaCaja.forEach((c) => {
        if (c && c.idSolicitud) {
          cMap[c.idSolicitud] = c;
        }
      });
      setCajaMap(cMap);

      // Cargar estudios para cada solicitud
      const solicitudesConEstudios = await Promise.all(
        listaSol.map(async (sol) => {
          try {
            const resEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(sol.idSolicitud);
            const estudiosArr = Array.isArray(resEst?.estudios)
              ? resEst.estudios
              : Array.isArray(resEst?.data)
              ? resEst.data
              : Array.isArray(resEst)
              ? resEst
              : [];
            return { ...sol, estudios: estudiosArr };
          } catch (e) {
            return { ...sol, estudios: [] };
          }
        })
      );

      setSolicitudes(solicitudesConEstudios);
      setSucursales(listaSuc);
      setConvenios(listaConv);

      // Sucursal por defecto: usuario actual o todas
      const uSucId = currentU.idSucursal || currentU.id_sucursal || currentU.sucursalId;
      const sucValida = listaSuc.find((s) => String(s.idSucursal || s.id) === String(uSucId));
      if (sucValida) {
        setSucursalSeleccionada(String(sucValida.idSucursal || sucValida.id));
      } else {
        setSucursalSeleccionada("todas");
      }
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para el reporte de convenios.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }
    return null;
  };

  // Verificadores de Estatus de Pago
  const isCancelada = (sol) => {
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    return est === "C" || est === "CANCELADA" || Boolean(sol.motivoCancelacion && String(sol.motivoCancelacion).trim() !== "");
  };

  const isPagada = (sol) => {
    if (isCancelada(sol)) return false;
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    if (est === "P" || est === "PAGADA" || est === "LIQUIDADA") return true;

    const totalPagar = Number(sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const adeudo = Number(sol.adeudo ?? sol.saldoPendiente ?? -1);
    if (adeudo >= 0 && adeudo <= 0.001) return true;

    const pagado = Number(sol.pagado ?? 0);
    const descuento = Number(sol.descuentoTotal ?? sol.descuento ?? 0);
    if (totalPagar > 0 && pagado >= (totalPagar - descuento)) return true;

    return false;
  };

  const isPorPagar = (sol) => {
    if (isCancelada(sol)) return false;
    if (isPagada(sol)) return false;
    return true;
  };

  // Normalizador de datos de solicitud para convenios
  const getDesgloseSolicitudConvenio = (sol) => {
    const cajaObj = cajaMap[sol.idSolicitud] || {};

    const totalNetoOriginal = Number(cajaObj.totalPagar ?? cajaObj.total ?? sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const descuentoTotal = Number(cajaObj.descuento ?? sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
    const iva = Number(cajaObj.iva ?? sol.iva ?? sol.montoIva ?? 0);

    // Importe (subtotal) procedente de la tabla caja (precio bruto de estudios sin descuento ni IVA)
    const importeGross = Number(
      cajaObj.subtotal ??
      sol.subtotal ??
      sol.subtotalCaja ??
      sol.subtotalSolicitud ??
      (totalNetoOriginal + descuentoTotal - iva)
    );

    // Total neto (subtotal - descuento + iva)
    const totalNeto = importeGross - descuentoTotal + iva;

    const pacienteNombre = [sol.nombrePaciente, sol.apPaternoPaciente, sol.apMaternoPaciente]
      .filter(Boolean)
      .join(" ")
      .trim() || sol.paciente || "Paciente";

    const convenioNombre = sol.nombreConvenio || sol.convenio || sol.empresa || "Sin Empresa / Convenio";

    let estadoPagoTexto = "Pendiente";
    if (isPagada(sol)) {
      estadoPagoTexto = "Pagado";
    }

    const estudiosArr = (sol.estudios || [])
      .filter((est) => !est.idEstudioPadre && !est.esEstudioInt)
      .map((est) => {
        const catInfo = catalogEstudiosMap[est.idEstudio] || catalogEstudiosMap[est.id] || {};
        const imp = Number(est.costo || est.precio || est.importe || catInfo.precio || catInfo.costo || 0);
        const desc = Number(est.descuento || 0);
        const codEst = est.codigo || est.codigoEstudio || catInfo.codigo || catInfo.codigoEstudio || catInfo.clave || catInfo.claveEstudio || `EST-${est.idEstudio}`;

        return {
          codigo: codEst,
          nombre: est.nombreEstudio || est.nombre || catInfo.nombre || `Estudio #${est.idEstudio}`,
          importe: imp,
          descuento: desc,
          totalNeto: imp - desc > 0 ? imp - desc : 0,
        };
      });

    return {
      idSolicitud: sol.idSolicitud,
      codigo: sol.codigo || sol.codigoSolicitud || String(sol.idSolicitud),
      paciente: pacienteNombre,
      convenio: convenioNombre,
      idConvenio: sol.idConvenio,
      importe: importeGross,
      descuento: descuentoTotal,
      iva: iva,
      totalNeto: totalNeto,
      estadoPago: estadoPagoTexto,
      estudios: estudiosArr,
    };
  };

  // --- FILTRADO DE SOLICITUDES EN EL RANGO ---
  const solicitudesFiltradas = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);

    if (!start || !end) return [];

    return solicitudes.filter((sol) => {
      // 1. Debe tener una empresa/convenio relacionada obligatoriamente
      const tieneEmpresa = Boolean(sol.idConvenio || (sol.nombreConvenio && sol.nombreConvenio.trim() !== ""));
      if (!tieneEmpresa) return false;

      // Excluir canceladas por regla de negocio
      if (isCancelada(sol)) return false;

      // 2. Filtro de sucursal
      if (sucursalSeleccionada !== "todas") {
        const sId = sol.idSucursal || sol.id_sucursal;
        if (String(sId) !== String(sucursalSeleccionada)) return false;
      }

      // 3. Filtro de empresa/convenio específica
      if (empresaSeleccionada !== "todas") {
        const cId = sol.idConvenio || sol.id_convenio;
        if (cId) {
          if (String(cId) !== String(empresaSeleccionada)) return false;
        } else {
          const nomConv = (sol.nombreConvenio || "").toLowerCase();
          const convObj = convenios.find((c) => String(c.idConvenio || c.id) === String(empresaSeleccionada));
          const targetNom = (convObj?.nombre || "").toLowerCase();
          if (targetNom && !nomConv.includes(targetNom)) return false;
        }
      }

      // 4. Filtro de fecha
      const dtSol = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.createdAt);
      if (!dtSol) return false;
      if (dtSol.getTime() < start.getTime() || dtSol.getTime() > end.getTime()) return false;

      // 5. Filtro de Estado de pago
      if (estadoPagoSeleccionado === "Pagados") {
        if (!isPagada(sol)) return false;
      } else if (estadoPagoSeleccionado === "Con adeudo") {
        if (!isPorPagar(sol)) return false;
      }

      return true;
    });
  }, [solicitudes, sucursalSeleccionada, empresaSeleccionada, estadoPagoSeleccionado, fechaDe, fechaA, convenios]);

  // AGRUPACIÓN POR COMPAÑÍA / CONVENIO PARA TODAS LAS SOLICITUDES EN EL RANGO (SIN PAGINACIÓN)
  const gruposCompania = useMemo(() => {
    const compMap = {};

    solicitudesFiltradas.forEach((sol) => {
      const desg = getDesgloseSolicitudConvenio(sol);
      const compKey = desg.convenio;
      if (!compMap[compKey]) {
        compMap[compKey] = [];
      }
      compMap[compKey].push(desg);
    });

    return Object.entries(compMap).map(([nombreCompania, listaSol]) => {
      const subtotal = listaSol.reduce(
        (acc, s) => ({
          importe: acc.importe + s.importe,
          descuento: acc.descuento + s.descuento,
          iva: acc.iva + s.iva,
          totalNeto: acc.totalNeto + s.totalNeto,
        }),
        { importe: 0, descuento: 0, iva: 0, totalNeto: 0 }
      );
      return {
        nombreCompania,
        solicitudes: listaSol,
        subtotal,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesFiltradas, catalogEstudiosMap, cajaMap]);

  // Total General acumulado completo sin paginación
  const totalGeneral = useMemo(() => {
    const listaDesglose = solicitudesFiltradas.map(getDesgloseSolicitudConvenio);
    return listaDesglose.reduce(
      (acc, s) => ({
        importe: acc.importe + s.importe,
        descuento: acc.descuento + s.descuento,
        iva: acc.iva + s.iva,
        totalNeto: acc.totalNeto + s.totalNeto,
      }),
      { importe: 0, descuento: 0, iva: 0, totalNeto: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesFiltradas, catalogEstudiosMap, cajaMap]);

  // --- EXPORTAR A PDF (TODAS LAS COMPAÑÍAS Y SOLICITUDES EN EL RANGO JUNTAS) ---
  const handleExportarPDF = async () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen convenios o solicitudes para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("landscape", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales.find((s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada)) || sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    doc.setFillColor(24, 35, 79);
    doc.rect(202, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 242.5, startY + 5, { align: "center" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 283, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 283, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 283, currY, { align: "right" });

    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("CONVENIOS EN SOLICITUDES", 14, currY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Reporte generado del ${fechaDe} al ${fechaA}`, 14, currY + 5);

    let nextY = currY + 12;

    const headCols = ["No.", "Solicitud", "Paciente", "Importe", "Descuento", "IVA", "Total"];
    if (!chkOcultarEstadoPago) {
      headCols.push("Estado");
    }

    // Agrupación completa por compañía en el rango de fechas
    const compMapCompleto = {};
    solicitudesFiltradas.forEach((sol) => {
      const desg = getDesgloseSolicitudConvenio(sol);
      const k = desg.convenio;
      if (!compMapCompleto[k]) compMapCompleto[k] = [];
      compMapCompleto[k].push(desg);
    });

    const bodyPDF = [];
    let consecutivoGlobal = 1;

    Object.entries(compMapCompleto).forEach(([nomComp, listaSol]) => {
      // Fila Encabezado de la Compañía (Azul con letras blancas)
      bodyPDF.push([
        {
          content: `Compañía: ${nomComp.toUpperCase()}`,
          colSpan: headCols.length,
          styles: { fontStyle: "bold", fillColor: [24, 35, 79], textColor: [255, 255, 255], halign: "left" },
        },
      ]);

      let subImp = 0,
        subDesc = 0,
        subIva = 0,
        subTot = 0;

      listaSol.forEach((sol) => {
        subImp += sol.importe;
        subDesc += sol.descuento;
        subIva += sol.iva;
        subTot += sol.totalNeto;

        const rowSol = [
          consecutivoGlobal++,
          sol.codigo,
          sol.paciente,
          `$${sol.importe.toFixed(2)}`,
          `$${sol.descuento.toFixed(2)}`,
          `$${sol.iva.toFixed(2)}`,
          `$${sol.totalNeto.toFixed(2)}`,
        ];
        if (!chkOcultarEstadoPago) {
          rowSol.push(sol.estadoPago);
        }
        bodyPDF.push(rowSol);

        if (chkMostrarDetalleSolicitud && sol.estudios && sol.estudios.length > 0) {
          sol.estudios.forEach((est) => {
            const rowEst = [
              "-",
              est.codigo,
              est.nombre,
              `$${est.importe.toFixed(2)}`,
              `$${est.descuento.toFixed(2)}`,
              "-",
              `$${est.totalNeto.toFixed(2)}`,
            ];
            if (!chkOcultarEstadoPago) {
              rowEst.push("-");
            }
            bodyPDF.push(rowEst);
          });
        }
      });

      // Fila Subtotal Compañía
      const rowSub = [
        { content: `Totales ${nomComp}:`, colSpan: 3, styles: { fontStyle: "bold", halign: "right" } },
        `$${subImp.toFixed(2)}`,
        `$${subDesc.toFixed(2)}`,
        `$${subIva.toFixed(2)}`,
        `$${subTot.toFixed(2)}`,
      ];
      if (!chkOcultarEstadoPago) {
        rowSub.push("");
      }
      bodyPDF.push(rowSub);
    });

    // Fila Total General
    const totGenCompleto = solicitudesFiltradas.map(getDesgloseSolicitudConvenio).reduce(
      (acc, s) => ({
        importe: acc.importe + s.importe,
        descuento: acc.descuento + s.descuento,
        iva: acc.iva + s.iva,
        totalNeto: acc.totalNeto + s.totalNeto,
      }),
      { importe: 0, descuento: 0, iva: 0, totalNeto: 0 }
    );

    const rowTotGen = [
      { content: "TOTAL GENERAL:", colSpan: 3, styles: { fontStyle: "bold", halign: "right", fillColor: [240, 244, 248] } },
      `$${totGenCompleto.importe.toFixed(2)}`,
      `$${totGenCompleto.descuento.toFixed(2)}`,
      `$${totGenCompleto.iva.toFixed(2)}`,
      `$${totGenCompleto.totalNeto.toFixed(2)}`,
    ];
    if (!chkOcultarEstadoPago) {
      rowTotGen.push("");
    }
    bodyPDF.push(rowTotGen);

    autoTable(doc, {
      startY: nextY,
      head: [headCols],
      body: bodyPDF,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 7.5, cellPadding: 1.8, halign: "center", valign: "middle" },
      columnStyles: {
        0: { width: 15 },
        1: { width: 35, fontStyle: "bold", halign: "left" },
        2: { halign: "left" },
        3: { width: 30 },
        4: { width: 30 },
        5: { width: 30, fontStyle: "bold" },
      },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 200);
      doc.text(`Página ${i} de ${pageCount}`, 283, 200, { align: "right" });
    }

        doc.save(`Convenios_Solicitudes_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Convenios y Solicitudes en PDF...",
      "Procesando documento"
    );
  };

  // --- EXPORTAR A EXCEL ---
  const handleExportarExcel = () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen convenios o solicitudes para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    const headerExcel = ["No.", "Compañía", "Solicitud / Código", "Paciente / Estudio", "Importe", "Descuento", "IVA", "Total"];
    if (!chkOcultarEstadoPago) {
      headerExcel.push("Estado");
    }

    const rowsExcel = [headerExcel];
    let idxNum = 1;

    // Agrupar por empresa
    const compMapCompleto = {};
    solicitudesFiltradas.forEach((sol) => {
      const desg = getDesgloseSolicitudConvenio(sol);
      const k = desg.convenio;
      if (!compMapCompleto[k]) compMapCompleto[k] = [];
      compMapCompleto[k].push(desg);
    });

    Object.entries(compMapCompleto).forEach(([nomComp, listaSol]) => {
      let subImp = 0,
        subDesc = 0,
        subIva = 0,
        subTot = 0;

      listaSol.forEach((sol) => {
        subImp += sol.importe;
        subDesc += sol.descuento;
        subIva += sol.iva;
        subTot += sol.totalNeto;

        const rowSol = [idxNum++, nomComp, sol.codigo, sol.paciente, sol.importe, sol.descuento, sol.iva, sol.totalNeto];
        if (!chkOcultarEstadoPago) {
          rowSol.push(sol.estadoPago);
        }
        rowsExcel.push(rowSol);

        if (chkMostrarDetalleSolicitud && sol.estudios && sol.estudios.length > 0) {
          sol.estudios.forEach((est) => {
            const rowEst = ["-", nomComp, est.codigo, est.nombre, est.importe, est.descuento, 0, est.totalNeto];
            if (!chkOcultarEstadoPago) {
              rowEst.push("-");
            }
            rowsExcel.push(rowEst);
          });
        }
      });

      // Fila Subtotal Compañía
      const rowSub = ["", `Totales ${nomComp}`, "", "", subImp, subDesc, subIva, subTot];
      if (!chkOcultarEstadoPago) rowSub.push("");
      rowsExcel.push(rowSub);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rowsExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Convenios y Solicitudes");

    XLSX.writeFile(workbook, `Reporte_Convenios_Solicitudes_${fechaDe}_al_${fechaA}.xlsx`);
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE CONVENIOS Y SOLICITUDES
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "100%", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal y Empresa */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal || suc.id} value={String(suc.idSucursal || suc.id)}>
                              {suc.nombre || suc.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Empresa:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={empresaSeleccionada}
                          onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                        >
                          <MenuItem value="todas">Todas las empresas</MenuItem>
                          {convenios.map((conv) => (
                            <MenuItem key={conv.idConvenio || conv.id} value={String(conv.idConvenio || conv.id)}>
                              {conv.nombre || conv.nombreConvenio || conv.empresa}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Estado de pago */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Estado de pago:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Select
                          size="small"
                          value={estadoPagoSeleccionado}
                          onChange={(e) => setEstadoPagoSeleccionado(e.target.value)}
                          sx={{ width: "240px" }}
                        >
                          <MenuItem value="Todos">Todos</MenuItem>
                          <MenuItem value="Pagados">Pagados</MenuItem>
                          <MenuItem value="Con adeudo">Con adeudo</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Adicionales */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Adicionales:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkMostrarDetalleSolicitud}
                                onChange={(e) => setChkMostrarDetalleSolicitud(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Mostrar detalle de la solicitud</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkOcultarEstadoPago}
                                onChange={(e) => setChkOcultarEstadoPago(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Ocultar estado de pago</Typography>}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            label="De:"
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <TextField
                            label="A:"
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <IconButton
                            onClick={fetchDatosIniciales}
                            sx={{ backgroundColor: "#006787", color: "white", "&:hover": { backgroundColor: "#014e65" } }}
                            title="Actualizar Datos"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Título informativo antes de la tabla */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#18234f" }}>
                  Reporte generado del {fechaDe} al {fechaA}
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={3} sx={{ width: "100%" }}>
                  {/* TABLA PRINCIPAL DE CONVENIOS Y SOLICITUDES (SIN PAGINACIÓN) */}
                  <Paper elevation={2} sx={{ p: 0, borderRadius: 3, overflow: "hidden", backgroundColor: "#ffffff", width: "100%" }}>
                    <Table size="small" sx={{ width: "100%", border: "1px solid #e2e8f0" }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem", width: "60px" }}>No.</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Solicitud</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Paciente</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Importe</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Descuento</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>IVA</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Total</TableCell>
                          {!chkOcultarEstadoPago && (
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Estado</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gruposCompania.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={chkOcultarEstadoPago ? 7 : 8} align="center" sx={{ py: 4, color: "#64748b" }}>
                              No existen solicitudes de convenio registradas en el rango de fechas seleccionado ({fechaDe} al {fechaA}).
                            </TableCell>
                          </TableRow>
                        ) : (
                          (() => {
                            let consecutivo = 1;
                            return gruposCompania.map((grupoComp) => (
                              <React.Fragment key={grupoComp.nombreCompania}>
                                {/* Fila Encabezado de la Compañía (Azul sólido con letras blancas) */}
                                <TableRow sx={{ backgroundColor: "#18234f" }}>
                                  <TableCell
                                    colSpan={chkOcultarEstadoPago ? 7 : 8}
                                    sx={{ backgroundColor: "#18234f", color: "#ffffff", fontWeight: "bold", py: 1.2, px: 2 }}
                                  >
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#ffffff" }}>
                                      Compañía: {grupoComp.nombreCompania.toUpperCase()}
                                    </Typography>
                                  </TableCell>
                                </TableRow>

                                {/* Solicitudes de esta compañía */}
                                {grupoComp.solicitudes.map((sol) => {
                                  const numFilaActual = consecutivo++;
                                  return (
                                    <React.Fragment key={sol.idSolicitud}>
                                      <TableRow sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
                                        <TableCell sx={{ fontWeight: "bold", color: "#64748b" }}>{numFilaActual}</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#006787" }}>{sol.codigo}</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#1e293b" }}>{sol.paciente}</TableCell>
                                        <TableCell align="center">${sol.importe.toFixed(2)}</TableCell>
                                        <TableCell align="center" sx={{ color: "#e65100" }}>${sol.descuento.toFixed(2)}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>${sol.iva.toFixed(2)}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787" }}>
                                          ${sol.totalNeto.toFixed(2)}
                                        </TableCell>
                                        {!chkOcultarEstadoPago && (
                                          <TableCell
                                            align="center"
                                            sx={{
                                              fontWeight: "bold",
                                              color: sol.estadoPago === "Pagado" ? "#2e7d32" : "#ed6c02",
                                            }}
                                          >
                                            {sol.estadoPago}
                                          </TableCell>
                                        )}
                                      </TableRow>

                                      {/* Subfilas de detalle de estudios si está seleccionado */}
                                      {chkMostrarDetalleSolicitud && sol.estudios && sol.estudios.length > 0 && (
                                        sol.estudios.map((est, idxEst) => (
                                          <TableRow key={idxEst} sx={{ backgroundColor: "#f8fafc" }}>
                                            <TableCell align="center" sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>-</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", color: "#006787", pl: 3 }}>{est.codigo}</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", fontWeight: "500", color: "#475569" }}>{est.nombre}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem" }}>${est.importe.toFixed(2)}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", color: "#e65100" }}>${est.descuento.toFixed(2)}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>-</TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", color: "#006787" }}>
                                              ${est.totalNeto.toFixed(2)}
                                            </TableCell>
                                            {!chkOcultarEstadoPago && (
                                              <TableCell align="center" sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>-</TableCell>
                                            )}
                                          </TableRow>
                                        ))
                                      )}
                                    </React.Fragment>
                                  );
                                })}

                                {/* Fila Subtotal por Compañía */}
                                <TableRow sx={{ backgroundColor: "#e2e8f0", borderBottom: "2px solid #cbd5e1" }}>
                                  <TableCell colSpan={3} align="right" sx={{ fontWeight: "bold", color: "#006787" }}>
                                    Totales {grupoComp.nombreCompania}:
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                    ${grupoComp.subtotal.importe.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#e65100" }}>
                                    ${grupoComp.subtotal.descuento.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                                    ${grupoComp.subtotal.iva.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", fontSize: "0.95rem" }}>
                                    ${grupoComp.subtotal.totalNeto.toFixed(2)}
                                  </TableCell>
                                  {!chkOcultarEstadoPago && <TableCell />}
                                </TableRow>
                              </React.Fragment>
                            ));
                          })()
                        )}

                        {/* Fila Total General acumulado */}
                        {gruposCompania.length > 0 && (
                          <TableRow sx={{ backgroundColor: "#f0f4f8", borderTop: "2px solid #006787" }}>
                            <TableCell colSpan={3} align="right" sx={{ fontWeight: "bold", color: "#18234f", fontSize: "1rem" }}>
                              TOTAL GENERAL:
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                              ${totalGeneral.importe.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#e65100", fontSize: "0.95rem" }}>
                              ${totalGeneral.descuento.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2", fontSize: "0.95rem" }}>
                              ${totalGeneral.iva.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", fontSize: "1.05rem" }}>
                              ${totalGeneral.totalNeto.toFixed(2)}
                            </TableCell>
                            {!chkOcultarEstadoPago && <TableCell />}
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botones flotantes laterales derechos para exportar PDF y Excel */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>

          {/* Botón Excel */}
          <Tooltip
            title="Exportar Reporte a Excel"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#2e7d32",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.5} color="#2e7d32" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default ConveniosSolicitudes;
